import { createRef, useState } from 'react';
import { useRouter } from 'next/router';
import { faXmark, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { sendSearchMetricData } from 'utils/metrics';
import styles from './ModalSearchBar.module.scss';

const baseSearchUrl = '/shop/all?search=';
const formatString = (inputStr) =>
  inputStr?.toLowerCase()?.replace(/\s|\./g, '') || '';

export default function ModalSearchBar({
  closeAllMenus,
  closeModal,
  showModal,
  searchInput,
}) {
  const router = useRouter();
  const formRef = createRef();
  const [recentSearchTerms, setRecentSearchTerms] = useState([]);

  const onSubmit = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const {
      search_term: { value: searchTerm = '' },
    } = formRef.current?.elements ?? {};

    if (searchTerm === '') return;
    await searchForTerm(searchTerm);
    if (
      !recentSearchTerms?.find(
        (term) => formatString(term) === formatString(searchTerm)
      )
    ) {
      const mostRecentFour = recentSearchTerms.slice(0, 4);
      setRecentSearchTerms([searchTerm, ...mostRecentFour]);
    }
  };

  const searchForTerm = async (searchTerm) => {
    sendSearchMetricData(searchTerm);
    const url = `${baseSearchUrl}${encodeURIComponent(searchTerm)}`;
    closeModal();
    closeAllMenus(url);
    router.push(url, undefined, { shallow: true });
  };

  return (
    <Modal
      fullscreen
      show={showModal}
      dialogClassName={styles.modal}
      onHide={closeModal}
    >
      <Modal.Header className={classNames('d-flex flex-column pb-0')}>
        <div className={classNames(styles.close_button)}>
          <Button
            className="p-0"
            variant="none"
            type="button"
            onClick={closeModal}
          >
            <span className={classNames('d-inline-block', styles.close)}>
              <FontAwesomeIcon icon={faXmark} />
            </span>
          </Button>
        </div>
        <div className={classNames('px-0 py-4', styles.search_bar_row)}>
          <Form
            ref={formRef}
            id="modal_search_form"
            className="bg-neutral-grey text-primary d-flex rounded-pill text-decoration-none"
            onSubmit={onSubmit}
          >
            <Button
              aria-label="Search Products"
              className={classNames('p-0 ms-6', styles.search_icon_wrapper)}
              variant="none"
              type="button"
              onClick={onSubmit}
            >
              <div className={classNames(styles.search_icon, 'text-primary')}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
            </Button>
            <Form.Control
              // modal with nothing but an input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className={classNames(
                styles.search_input,
                'rounded-2 bg-transparent text-primary text-decoration-none'
              )}
              id="search_term"
              defaultValue={searchInput}
              type="search"
              onSubmit={onSubmit}
            />
          </Form>
        </div>
      </Modal.Header>
      <Modal.Body
        className={classNames(
          'd-flex w-100 flex-column align-items-center',
          styles.modal_body
        )}
      >
        {recentSearchTerms?.length > 0 && (
          <div className={`${styles.recent_search_row}`}>
            <h2 className="font-frl fw-900 text-primary">
              Recently Searched Products
            </h2>
            {recentSearchTerms?.map((term) => {
              return (
                <div key={`${term}`} className="border-bottom">
                  <Button
                    className="p-3 me-4"
                    variant="none"
                    type="button"
                    onClick={() => {
                      searchForTerm(term);
                    }}
                  >
                    <span className="fs-10 lh-1 mb-2 mt-15 text-primary">
                      {term}
                    </span>
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
